import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateWithPreference } from './utils/navigationHelper';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function EmailTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiFetch('/api/email-templates');
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/email-templates/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchTemplates();
      } else {
        alert('Failed to delete email template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting email template');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArea = !areaFilter || template.area === areaFilter;
    return matchesSearch && matchesArea;
  });

  const areaLabels = {
    quote: 'Quotes',
    invoice: 'Invoices',
    job: 'Jobs',
    sales_order: 'Sales Orders'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading email templates...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <PageHeader
        title="Email Templates"
        subtitle={`${templates.length} total`}
        backTo="/reports-and-templates"
        backLabel="Reports & Templates"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/email-templates/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ New Template
          </button>
        }
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* Search & Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Areas</option>
              <option value="quote">Quotes</option>
              <option value="invoice">Invoices</option>
              <option value="job">Jobs</option>
              <option value="sales_order">Sales Orders</option>
            </select>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition cursor-pointer border border-gray-200"
              onClick={() => navigateWithPreference(navigate, `/email-templates/${template.id}`, 'email-templates')}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {template.name}
                    </h3>
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                      {areaLabels[template.area] || template.area}
                    </span>
                    {template.is_default && (
                      <span className="inline-block ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(template.id);
                    }}
                    className="text-gray-400 hover:text-red-600 transition"
                  >
                    🗑️
                  </button>
                </div>

                <div className="mb-3">
                  <div className="text-sm text-gray-600 font-medium mb-1">Subject:</div>
                  <div className="text-sm text-gray-800 truncate">
                    {template.subject}
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  Updated: {new Date(template.updated_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-4">No email templates found</div>
            <button
              onClick={() => navigate('/email-templates/new')}
              className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition"
            >
              Create Your First Template
            </button>
          </div>
        )}

        {/* Count */}
        <div className="text-center text-gray-600 text-sm mt-6">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          {searchTerm && ' matching search'}
        </div>
      </div>
    </div>
  );
}

export default EmailTemplates;
